/**
 * Web Push + PWA helpers for Habla.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return reg;
  } catch (e) {
    console.warn('SW registration failed', e);
    return null;
  }
}

async function getRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  let reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) reg = await registerServiceWorker();
  if (reg && navigator.serviceWorker.controller === null) {
    try { await navigator.serviceWorker.ready; } catch (e) { /* ignore */ }
  }
  return reg;
}

export async function getPushStatus() {
  try {
    const res = await axios.get(`${API}/push/status`, { withCredentials: true });
    return res.data;
  } catch (e) {
    return { enabled: false, devices: 0 };
  }
}

export async function enablePush() {
  if (!isPushSupported()) {
    throw new Error('Dein Browser unterstützt keine Benachrichtigungen.');
  }
  if (isIOS() && !isStandalonePWA()) {
    throw new Error('Auf iPhone musst du die App zuerst zum Home-Bildschirm hinzufügen.');
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new Error('Benachrichtigungen wurden abgelehnt. Aktiviere sie in deinen Browser-Einstellungen.');
  }
  const reg = await getRegistration();
  if (!reg) throw new Error('Service Worker konnte nicht registriert werden.');
  await navigator.serviceWorker.ready;

  // Get VAPID public key
  const keyRes = await axios.get(`${API}/push/vapid-public-key`);
  const vapidKey = keyRes.data.public_key;
  if (!vapidKey) throw new Error('Push-Dienst nicht verfügbar.');

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }

  await axios.post(
    `${API}/push/subscribe`,
    { subscription: sub.toJSON() },
    { withCredentials: true }
  );
  return sub;
}

export async function disablePush() {
  try {
    const reg = await getRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
      await axios.post(
        `${API}/push/unsubscribe`,
        { endpoint },
        { withCredentials: true }
      );
    }
  } catch (e) {
    console.warn('disablePush error', e);
  }
}

export async function sendTestPush() {
  const res = await axios.post(`${API}/push/test`, {}, { withCredentials: true });
  return res.data;
}

export const PUSH_PROMPT_KEY = 'hca-push-prompt-dismissed-v1';
export function dismissPushPrompt() {
  try { localStorage.setItem(PUSH_PROMPT_KEY, '1'); } catch (e) { /* ignore */ }
}
export function pushPromptDismissed() {
  try { return localStorage.getItem(PUSH_PROMPT_KEY) === '1'; } catch (e) { return false; }
}
